import React from 'react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { Flex } from '@klarna/mp-ui';
import { Row, Column } from 'mage-components';
import { connect } from 'react-redux';
import { translator } from 'mage-i18n';
import { generateCyElemId, generateCyElemIdWithValue } from 'moet-test-utils/cypress/utils';

import SummaryColumn from '@/components/SummaryColumn';
import YesNoFieldSummary from '@/components/YesNoFieldSummary';
import { withFeatureToggledValidation } from '@/containers/withFeatureToggledValidation';
import { getEnrichedVatCountries } from '@/selectors/options';

import MerchantProductTermsAndConditions from './MerchantProductTermsAndConditions';
import SummaryAddress from './SummaryAddress';
import { CY_APPLICATION_OVERVIEW_PAGE_IDS } from './constants';

const tPrefix = 'products-fe.components.store_details_summary';
const tOrganizationEntityType = type => `products-fe.options.organization_entity_types.${type}`;

const {
  BUSINESS_ADDRESS_BLOCK,
  COMPANY_NAME,
  COUNTRY,
  EMAIL,
  GOODS_CATEGORY,
  OPERATING_ADDRESS_BLOCK,
  ORGANIZATION_ENTITY_TYPE,
  REGISTRATION_ID,
  STORE_NAME,
  TOC,
  VAT_ID,
  WEBSITE,
} = CY_APPLICATION_OVERVIEW_PAGE_IDS;

const SummaryStoreDetails = ({
  enrichedVatCountries,
  isUnmanagedKyc,
  storeDetails: { businessDetails, country, tocData },
  t,
  forcedValidationConfig: {
    registrationId: { translationKey: tKeyRegistrationId = country } = {},
  } = {},
}) => (
  <Flex>
    <Row>
      <SummaryColumn
        titleKey={`${tPrefix}.storeName.label`}
        content={businessDetails.storeName}
        {...generateCyElemId(STORE_NAME, window)}
      />
      <SummaryColumn
        titleKey={`${tPrefix}.country.label`}
        content={country}
        {...generateCyElemId(COUNTRY, window)}
      />
      <SummaryColumn
        titleKey={`${tPrefix}.website.label`}
        content={businessDetails.website}
        {...generateCyElemId(WEBSITE, window)}
      />
      <SummaryColumn
        titleKey={`${tPrefix}.email.label`}
        content={businessDetails.email}
        {...generateCyElemId(EMAIL, window)}
      />
      {(country !== 'US' || businessDetails.registered) && (
        <Column columns={{ tablet: 6 }}>
          <YesNoFieldSummary
            country={tKeyRegistrationId}
            booleanAnswer={businessDetails.registered}
            titleYesKey={`${tPrefix}.registrationId.label`}
            titleNoKey={`${tPrefix}.registered.label`}
            yesAnswer={businessDetails.registrationId}
            {...generateCyElemIdWithValue(REGISTRATION_ID, businessDetails.registrationId, window)}
          />
        </Column>
      )}
      {country === 'US' ? (
        <SummaryColumn
          titleKey={`${tPrefix}.organizationEntityType.label`}
          content={t(tOrganizationEntityType(businessDetails.organizationEntityType))}
          {...generateCyElemIdWithValue(
            ORGANIZATION_ENTITY_TYPE,
            businessDetails.organizationEntityType,
            window,
          )}
        />
      ) : (
        (!enrichedVatCountries.includes(country) || !businessDetails.registered) && (
          <Column columns={{ tablet: 6 }}>
            <YesNoFieldSummary
              country={country}
              booleanAnswer={businessDetails.subjectToVat}
              titleYesKey={`${tPrefix}.vatId.label`}
              titleNoKey={`${tPrefix}.subjectToVat.label`}
              yesAnswer={businessDetails.vatId}
              {...generateCyElemIdWithValue(VAT_ID, businessDetails.vatId, window)}
            />
          </Column>
        )
      )}
      <SummaryColumn
        titleKey={`${tPrefix}.goodsCategory.label`}
        content={t(`products-fe.options.goods_categories.${businessDetails.goodsCategory}`)}
        {...generateCyElemIdWithValue(GOODS_CATEGORY, businessDetails.goodsCategory, window)}
      />
      {isUnmanagedKyc && (
        <SummaryColumn
          titleKey={`${tPrefix}.toc_data.label`}
          content={<MerchantProductTermsAndConditions tocData={tocData} country={country} />}
          {...generateCyElemId(TOC, window)}
        />
      )}
      {businessDetails.companyName && (
        <SummaryColumn
          titleKey={`${tPrefix}.${
            businessDetails.registered ? 'registeredCompanyName' : 'companyName'
          }.label`}
          content={businessDetails.companyName}
          {...generateCyElemId(COMPANY_NAME, window)}
        />
      )}
    </Row>
    {businessDetails.address && (
      <SummaryAddress
        address={businessDetails.address}
        titleKey={`${tPrefix}.address.label`}
        {...generateCyElemId(BUSINESS_ADDRESS_BLOCK, window)}
      />
    )}
    {businessDetails.operatingAddress && (
      <SummaryAddress
        address={businessDetails.operatingAddress}
        titleKey={`${tPrefix}.operatingAddress.label`}
        {...generateCyElemId(OPERATING_ADDRESS_BLOCK, window)}
      />
    )}
  </Flex>
);

SummaryStoreDetails.propTypes = {
  enrichedVatCountries: PropTypes.arrayOf(PropTypes.string).isRequired,
  isUnmanagedKyc: PropTypes.bool.isRequired,
  storeDetails: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  forcedValidationConfig: PropTypes.object,
};

export default compose(
  connect(state => ({
    t: translator(state),
    enrichedVatCountries: getEnrichedVatCountries(state),
  })),
  withFeatureToggledValidation('registrationId'),
)(SummaryStoreDetails);
